import { get } from 'lodash';
import { VEGA_SPEC_AXIS_SETTINGS } from 'constants/chart-spec';
import { CHART_CATEGORIES } from 'constants/chart-categories';
import { generateVegaLabelTextExpression } from 'utils/chart/vega-expressions';
import { generateVegaTooltip } from 'utils/chart/tooltips';
import {
  generateVegaSortParams,
  generateVegaTypeCoercionTransforms,
  generateVegaSortingAndLimitingTransforms,
} from 'utils/chart/transforms';

export default {
  name: 'Heatmap',
  displayName: 'Heatmap',
  order: 0,
  flavour: 'discrete',
  iconValue: 'heatmap',
  category: CHART_CATEGORIES.GRID,
  channels: [
    {
      name: 'x',
      displayName: 'X Axis',
      required: true,
      channelType: 'category',
      intent: 'category-axis',
    },
    {
      name: 'y',
      displayName: 'Y Axis',
      required: false,
      channelType: 'category',
      intent: 'series',
    },
    {
      name: 'color',
      displayName: 'Intensity',
      required: true,
      channelType: 'aggregation',
      intent: 'value-axis',
    },
  ],
  specFn: (encodings, customisations) => {
    const sortParams = generateVegaSortParams(encodings);
    const color = get(
      customisations.options.colorContinuous,
      'value.color',
      'yellowgreenblue'
    );
    const reverseColor = get(
      customisations.options.colorContinuous,
      'value.reverse',
      false
    );

    const marksUpdate = {
      x: { scale: 'x', field: 'x' },
      width: { scale: 'x', band: true },
      fill: { scale: 'color', field: 'color' },
      tooltip: { signal: generateVegaTooltip(encodings, customisations) },
    };
    const scales = [
      {
        name: 'x',
        type: 'band',
        domain: { data: 'sortedData', field: 'x', sort: sortParams.x },
        range: [0, { signal: 'width' }],
        paddingInner: 0.1,
        paddingOuter: 0.05,
      },
      {
        name: 'color',
        type: 'sequential',
        domain: { data: 'sortedData', field: 'color' },
        range: 'heatmap',
        nice: false,
        zero: false,
        reverse: reverseColor,
      },
    ];
    const axes = [
      {
        ...VEGA_SPEC_AXIS_SETTINGS,
        scale: 'x',
        orient: 'bottom',
        grid: false,
        title: get(encodings, 'x.axis.title', ''),
        encode: {
          labels: {
            update: {
              angle: { value: 270 },
              align: { value: 'right' },
              baseline: { value: 'middle' },
              text: {
                signal: generateVegaLabelTextExpression(
                  encodings.x,
                  customisations.channels.x
                ),
              },
            },
          },
        },
        zindex: 1,
      },
    ];
    if (encodings.y) {
      marksUpdate.y = { scale: 'y', field: 'y' };
      marksUpdate.height = { scale: 'y', band: true };
      scales.push({
        name: 'y',
        type: 'band',
        domain: { data: 'transformedData', field: 'y', sort: sortParams.y },
        range: [0, { signal: 'height' }],
        paddingInner: 0.1,
        paddingOuter: 0.05,
      });
      axes.push({
        ...VEGA_SPEC_AXIS_SETTINGS,
        scale: 'y',
        orient: 'left',
        grid: false,
        title: get(encodings, 'y.axis.title', ''),
        zindex: 1,
        encode: {
          labels: {
            update: {
              text: {
                signal: generateVegaLabelTextExpression(
                  encodings.y,
                  customisations.channels.y
                ),
              },
            },
          },
        },
      });
    } else {
      marksUpdate.y = { value: 0 };
      marksUpdate.y2 = { field: { group: 'height' } };
    }
    return {
      autosize: {
        type: 'fit',
        contains: 'padding',
      },
      style: 'cell',
      data: [
        {
          name: 'transformedData',
          source: 'values',
          transform: generateVegaTypeCoercionTransforms('color'),
        },
        {
          name: 'sortedData',
          source: 'values',
          transform: generateVegaSortingAndLimitingTransforms(encodings),
        },
      ],
      marks: [
        {
          name: 'marks',
          type: 'rect',
          style: ['rect'],
          from: { data: 'sortedData' },
          encode: { update: marksUpdate },
        },
      ],
      scales: scales,
      axes: axes,
      legends: [
        {
          title: get(encodings, 'color.legend.title'),
          fill: 'color',
          type: 'gradient',
          encode: {
            labels: {
              update: {
                text: {
                  signal: generateVegaLabelTextExpression(
                    encodings.color,
                    customisations.channels.color,
                    { withDecimals: false }
                  ),
                },
              },
            },
          },
        },
      ],
      config: {
        style: { cell: { stroke: 'transparent' } },
        range: { heatmap: { scheme: color } },
      },
    };
  },
};
